/* ==========================================================================
   Ladder targets on the Signals "Capital by Expiration" card (CIO 2026-10-28 / 2026-10-29).

   GET /api/get_ladder_status runs the Screener's OWN ladder (strategy/ladder_priority.py) on the held
   book, so this card and the Screener can never disagree about a rung's target or status. Two bases,
   both labelled (CIO decision 2026-10-29, by question: "Both, clearly labelled"):
     - the bar = market value as a share of the WHOLE held book (unchanged);
     - the ladder column = cost as a share of the 18m+ (buyable) book vs the target.
   A line under 18 months (e.g. DEC2027) is outside the ladder: bar only, no target.
   A listed rung the fund does not hold yet appears with an empty bar and its target, so the gap is
   visible. Display only: nothing here changes a score, an order or a rule.
   ========================================================================== */

export type LadderRung = {
  expiry: string;
  dte?: number | null;
  bucket?: string;
  bucketLabel?: string;
  held?: boolean;            // the fund holds this rung (the feed carries no dollar amounts)
  sharePct?: number | null;
  targetPct?: number | null;
  gapPct?: number | null;
  toTargetUsd?: number | null;
  status?: "room" | "on_target" | "full" | "outside_ladder" | string;
  statusLabel?: string;
  statusWhy?: string;
  rollingOff?: boolean;
  daysUntilUnder18m?: number | null;
};

export type LadderStatusPayload = {
  active?: boolean;
  mode?: string;
  fund?: string;
  basis?: string;
  shareOf?: string;
  bucketTargets?: Record<string, number>;
  bandPct?: number | null;
  capPct?: number | null;
  targetBasis?: "listed" | "held" | string;
  listedSource?: { savedAtUtc?: string | null; etDate?: string | null; blob?: string | null; error?: string | null };
  rungs?: LadderRung[];
  otherHeld?: { expiry: string; held?: boolean }[];
  note?: string;
  generatedAtUtc?: string;
};

export type ExpiryMarketBucket = { exp: string; dte: number | null; notional: number };

export type LadderRowView =
  | { kind: "rung"; rung: LadderRung }
  | { kind: "outside"; reason: string }
  | { kind: "none" };

export type CapitalRow = {
  exp: string;
  dte: number | null;
  notional: number;          // market value, 0 when the expiry has no priced holding row
  priced: boolean;           // the holdings rows gave this expiry a market value (the bar)
  held: boolean;             // priced, or the ladder reports a held amount at cost (a held line with no usable mark)
  ladder: LadderRowView;
};

const iso10 = (s: string | null | undefined): string => (s ? String(s).slice(0, 10) : "");

/**
 * Merge the market-value buckets (whole book, from the holdings rows) with the ladder rungs.
 * Every priced expiry keeps its row; every ladder rung gets a row. A rung with no priced row is either NOT HELD
 * (held false: an empty bar and its target) or HELD WITH NO MARKET PRICE (held true: say so, never "not held");
 * unpriced rungs are only added when the caller says the holdings rows are complete (addUnpricedRungs);
 * rows are ordered by DTE (unknown DTE last), the card's existing order.
 */
export function mergeCapitalRows(
  buckets: ExpiryMarketBucket[],
  ladder: LadderStatusPayload | null,
  opts: { addUnpricedRungs?: boolean } = {},
): CapitalRow[] {
  // Rungs with no priced row are added ONLY when the holdings rows are loaded and unfiltered. Otherwise an empty or
  // filtered holdings set would make every held rung read "held · no market price" (review 2026-10-29, finding 4).
  const addUnpriced = opts.addUnpricedRungs !== false;
  const active = !!(ladder && ladder.active);
  const rungByExp = new Map<string, LadderRung>();
  if (active) for (const r of ladder!.rungs || []) rungByExp.set(iso10(r.expiry), r);
  const otherHeld = new Set<string>(active ? (ladder!.otherHeld || []).map((o) => iso10(o.expiry)) : []);

  const rows = new Map<string, CapitalRow>();
  for (const b of buckets) {
    const key = iso10(b.exp) || b.exp;
    const rung = rungByExp.get(key);
    rows.set(key, {
      exp: b.exp,
      dte: b.dte,
      notional: b.notional,
      priced: true,
      held: true,
      ladder: rung ? { kind: "rung", rung }
        : active && (otherHeld.has(key) || (b.dte != null && b.dte < 540))
          ? { kind: "outside", reason: "under 18 months: outside the ladder (no target, not in its total)" }
          : { kind: "none" },
    });
  }
  for (const [key, rung] of rungByExp) {
    if (rows.has(key) || !addUnpriced) continue;
    const heldAtCost = rung.held === true;
    rows.set(key, { exp: key, dte: rung.dte ?? null, notional: 0, priced: false, held: heldAtCost, ladder: { kind: "rung", rung } });
  }
  return Array.from(rows.values()).sort((a, b) => {
    if (a.dte == null && b.dte == null) return a.exp.localeCompare(b.exp);
    if (a.dte == null) return 1;
    if (b.dte == null) return -1;
    return a.dte - b.dte;
  });
}

const pct1 = (x: number) => `${(x * 100).toFixed(1)}%`;

/** "38.8% vs 35.0% target" -- the ladder's own share (cost, 18m+) beside its target. */
export function ladderShareText(r: LadderRung): string {
  const share = typeof r.sharePct === "number" && Number.isFinite(r.sharePct) ? pct1(r.sharePct) : "—";
  const target = typeof r.targetPct === "number" && Number.isFinite(r.targetPct) ? pct1(r.targetPct) : "—";
  return `${share} vs ${target} target`;
}

/** Signed gap in points: "+3.8 pts over" / "6.3 pts under" / "on target". */
export function ladderGapText(r: LadderRung): string {
  if (typeof r.sharePct !== "number" || typeof r.targetPct !== "number") return "";
  const d = (r.sharePct - r.targetPct) * 100;
  if (Math.abs(d) < 0.05) return "at target";
  return d > 0 ? `${d.toFixed(1)} pts over` : `${Math.abs(d).toFixed(1)} pts under`;
}

const STALE_LIST_DAYS = 14;

/** Days between the saved run's ET date and today (local), or null. */
export function listAgeDays(etDate: string | null | undefined, today: Date = new Date()): number | null {
  const d = iso10(etDate);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  const [y, m, dd] = d.split("-").map(Number);
  const t0 = Date.UTC(y, m - 1, dd);
  const t1 = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((t1 - t0) / 86_400_000);
}

/** Footnote: where the targets come from, how old the expiry list is, and the fund. */
export function ladderFootnote(p: LadderStatusPayload | null, fetchError: string | null, today: Date = new Date()): string {
  if (!p) return fetchError ? `Ladder targets unavailable (${fetchError}).` : "Ladder targets loading…";
  if (!p.active) return p.note || "Ladder targets not shown.";
  const t = p.bucketTargets || {};
  const parts = [["24_30m", "24-30m"], ["18_24m", "18-24m"], ["30m_plus", "30m+"]]
    .filter(([k]) => typeof t[k] === "number")
    .map(([k, lbl]) => `${lbl} ${(t[k] * 100).toFixed(0)}%`);
  const age = listAgeDays(p.listedSource?.etDate, today);
  const stale = age != null && age > STALE_LIST_DAYS ? ` — ${age} days old; run the Screener to refresh it` : "";
  const src = p.targetBasis === "listed"
    ? `split over every listed expiry (IBKR chain list from the Screener run saved ${iso10(p.listedSource?.etDate) || "—"}${stale})`
    : "split over the HELD expiries only (no saved Screener run with a chain list; run the Screener to restore the full split)";
  const band = typeof p.bandPct === "number" && Number.isFinite(p.bandPct) ? `${(p.bandPct * 100).toFixed(0)}+ pts` : "beyond the band";
  const cap = typeof p.capPct === "number" && Number.isFinite(p.capPct) ? ` or above the ${(p.capPct * 100).toFixed(0)}% per-expiry cap` : "";
  return `Bars = the Screener's ladder: share of the 18m+ LEAPS book at cost vs target (${parts.join(", ")}), ${src}. ` +
    `Room = ${band} under target; Full = ${band} over${cap}. ${p.fund ? `Book: ${p.fund}.` : ""}`.trim();
}

/* --------------------------------------------------------------------------
   Ladder bar geometry (CIO 2026-10-29, by question: "Ladder bar + target tick").
   One bar per rung = its share of the 18m+ book at cost, a tick at the target and a faint band of
   +-bandPct around it (the "on target" zone). Every bar on the card shares ONE scale, so lengths
   compare across rows: the largest of (cap, any share, any target + band), rounded up to 10 pts.
   -------------------------------------------------------------------------- */
export type LadderBarGeometry = { fillPct: number; tickPct: number | null; bandLoPct: number | null; bandHiPct: number | null };

export function ladderScale(p: LadderStatusPayload | null): number {
  if (!p || !p.active) return 1;
  const band = typeof p.bandPct === "number" && Number.isFinite(p.bandPct) ? p.bandPct : 0;
  let m = typeof p.capPct === "number" && Number.isFinite(p.capPct) ? p.capPct : 0;
  for (const r of p.rungs || []) {
    if (typeof r.sharePct === "number" && Number.isFinite(r.sharePct)) m = Math.max(m, r.sharePct);
    if (typeof r.targetPct === "number" && Number.isFinite(r.targetPct)) m = Math.max(m, r.targetPct + band);
  }
  return Math.min(1, Math.max(0.3, Math.ceil(m * 10 - 1e-9) / 10));
}

const clampPct = (x: number) => Math.max(0, Math.min(100, x));

export function ladderBarGeometry(r: LadderRung, scale: number, bandPct: number | null | undefined): LadderBarGeometry {
  const s = scale > 0 ? scale : 1;
  const share = typeof r.sharePct === "number" && Number.isFinite(r.sharePct) ? r.sharePct : 0;
  const t = typeof r.targetPct === "number" && Number.isFinite(r.targetPct) ? r.targetPct : null;
  const b = typeof bandPct === "number" && Number.isFinite(bandPct) ? bandPct : 0;
  return {
    fillPct: clampPct((share / s) * 100),
    tickPct: t == null ? null : clampPct((t / s) * 100),
    bandLoPct: t == null ? null : clampPct(((t - b) / s) * 100),
    bandHiPct: t == null ? null : clampPct(((t + b) / s) * 100),
  };
}

/** Bar fill colour by status (same hues as the Screener's chips; a rolling-off room rung is amber, not green). */
export function ladderFill(status?: string, rollingOff?: boolean): string {
  if (status === "room") return rollingOff ? "rgba(245, 158, 11, 0.38)" : "rgba(16, 185, 129, 0.60)";
  if (status === "full") return "rgba(245, 158, 11, 0.62)";
  if (status === "on_target") return "rgba(148, 163, 184, 0.62)";
  return "rgba(148, 163, 184, 0.30)";
}

/** "+3.8" / "-5.9" / "0.0" -- signed points vs target, for the compact numbers column. */
export function ladderGapSigned(r: LadderRung): string {
  if (typeof r.sharePct !== "number" || typeof r.targetPct !== "number") return "";
  const d = (r.sharePct - r.targetPct) * 100;
  return `${d > 0.05 ? "+" : d < -0.05 ? "−" : ""}${Math.abs(d).toFixed(1)}`;
}

/* --------------------------------------------------------------------------
   Bucket summary (CIO 2026-10-29, by question: "Bucket summary beside it"). The ladder's targets are set
   per BUCKET (24-30m / 18-24m / 30m+) and split over the listed expiries; this rolls the rungs back up.
   The feed gives a status per rung only, so a bucket's share is the SUM of its rungs' shares (same cost,
   18m+ basis) and its status uses the same band rule as a rung (no per-expiry cap at bucket level).
   -------------------------------------------------------------------------- */
export type LadderBucketRow = {
  bucket: string;
  label: string;
  sharePct: number;
  targetPct: number;
  status: "room" | "on_target" | "full";
  rungs: number;
};

const BUCKET_ORDER: [string, string][] = [["24_30m", "24-30m"], ["18_24m", "18-24m"], ["30m_plus", "30m+"]];

export function ladderBuckets(p: LadderStatusPayload | null): LadderBucketRow[] {
  if (!p || !p.active) return [];
  const band = typeof p.bandPct === "number" && Number.isFinite(p.bandPct) ? p.bandPct : 0.03;
  const targets = p.bucketTargets || {};
  const out: LadderBucketRow[] = [];
  for (const [key, label] of BUCKET_ORDER) {
    const t = targets[key];
    if (typeof t !== "number" || !Number.isFinite(t)) continue;
    const rs = (p.rungs || []).filter((r) => r.bucket === key);
    if (!rs.length) continue;                       // no listed rung in this bucket: nothing to measure against
    const share = rs.reduce((s, r) => s + (typeof r.sharePct === "number" && Number.isFinite(r.sharePct) ? r.sharePct : 0), 0);
    const status = share >= t + band - 1e-9 ? "full" : share <= t - band + 1e-9 ? "room" : "on_target";
    out.push({ bucket: key, label, sharePct: share, targetPct: t, status, rungs: rs.length });
  }
  return out;
}

/** One scale for the bucket bars (their shares run larger than a single rung's). */
export function bucketScale(rows: LadderBucketRow[], bandPct: number | null | undefined): number {
  const b = typeof bandPct === "number" && Number.isFinite(bandPct) ? bandPct : 0;
  const m = rows.reduce((acc, r) => Math.max(acc, r.sharePct, r.targetPct + b), 0);
  return Math.min(1, Math.max(0.3, Math.ceil(m * 10 - 1e-9) / 10));
}

/** Rungs with room, the ones that are not rolling off first (largest gap first), then the rolling-off ones. */
export function roomRungs(p: LadderStatusPayload | null): LadderRung[] {
  if (!p || !p.active) return [];
  const gap = (r: LadderRung) => (typeof r.targetPct === "number" && typeof r.sharePct === "number" ? r.targetPct - r.sharePct : 0);
  return (p.rungs || [])
    .filter((r) => r.status === "room")
    .sort((a, b) => (Number(!!a.rollingOff) - Number(!!b.rollingOff)) || (gap(b) - gap(a)) || String(a.expiry).localeCompare(String(b.expiry)));
}
